<template>
  <nav class="bg-white/80 dark:bg-gray-900/80 backdrop-blur border-b border-gray-200 dark:border-gray-800 sticky top-0 z-30">
    <div class="mx-auto max-w-screen-2xl px-3 sm:px-4 lg:px-8 w-full">
      <div class="flex h-14 sm:h-16 items-center justify-between">
        <!-- Left side: Logo and Navigation -->
        <div class="flex items-center gap-2">
          <!-- Navbar position toggle - Only show on desktop and not in mobile mode -->
          <button
            v-if="!isMobile"
            type="button"
            class="hidden md:inline-flex items-center justify-center rounded-md bg-gray-200 dark:bg-gray-800 p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 touch-manipulation"
            :title="isVertical ? 'Cambiar a navbar horizontal' : 'Cambiar a navbar vertical'"
            @click="$emit('toggle-navbar-position')"
          >
            <span class="sr-only">Cambiar posición del navbar</span>
            <ViewColumnsIcon
              v-if="isHorizontal"
              class="block h-5 w-5"
              aria-hidden="true"
            />
            <Bars2Icon
              v-else
              class="block h-5 w-5"
              aria-hidden="true"
            />
          </button>
          
          <!-- Mobile menu button - Only show when authenticated -->
          <button
            v-if="authStore.isAuthenticated"
            type="button"
            class="inline-flex md:hidden items-center justify-center rounded-md bg-gray-200 dark:bg-gray-800 p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 touch-manipulation"
            aria-controls="mobile-menu"
            aria-expanded="false"
            @click="$emit('toggle-mobile-menu')"
          >
            <span class="sr-only">{{ t('app.actions.open_main_menu') }}</span>
            <Bars3Icon
              v-if="!isMobileMenuOpen"
              class="block h-6 w-6"
              aria-hidden="true"
            />
            <XMarkIcon
              v-else
              class="block h-6 w-6"
              aria-hidden="true"
            />
          </button>
          
          <!-- Logo -->
          <NavbarLogo :show-restaurant-name="true" />
          
          <!-- Desktop navigation links -->
          <NavbarNavigation layout="horizontal" />
        </div>

        <!-- Desktop user menu -->
        <div
          v-if="authStore.isAuthenticated"
          class="hidden md:block"
        >
          <div class="ml-4 flex items-center md:ml-6 gap-2 sm:gap-3">
            <!-- Alerts Badge - Only show in restaurant subdomains and Cloud (not Electron) -->
            <button
              v-if="showAlerts"
              class="relative rounded-full p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 touch-manipulation"
              :aria-label="'Alertas de suscripción'"
              @click="$emit('notification-click')"
            >
              <BellAlertIcon class="h-5 w-5 sm:h-6 sm:w-6" />
              <span
                v-if="unreadAlertCount > 0"
                class="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full min-w-[18px]"
              >
                {{ unreadAlertCount > 9 ? '9+' : unreadAlertCount }}
              </span>
            </button>

            <UserMenu 
              layout="horizontal" 
              :unread-alert-count="unreadAlertCount"
              @notification-click="$emit('notification-click')"
              @logout="$emit('logout')"
            />
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { 
  Bars3Icon, 
  XMarkIcon, 
  BellAlertIcon, 
  ViewColumnsIcon, 
  Bars2Icon 
} from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { hasRestaurantContext } from '@/utils/subdomain';
import { platformFeatures } from '@/utils/platform';
import NavbarLogo from './NavbarLogo.vue';
import NavbarNavigation from './NavbarNavigation.vue';
import UserMenu from './UserMenu.vue';

interface Props {
  isMobile?: boolean;
  isMobileMenuOpen?: boolean;
  isVertical?: boolean;
  isHorizontal?: boolean;
  unreadAlertCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  isMobile: false,
  isMobileMenuOpen: false,
  isVertical: false,
  isHorizontal: true,
  unreadAlertCount: 0
});

const emit = defineEmits<{
  'toggle-mobile-menu': [];
  'toggle-navbar-position': [];
  'notification-click': [];
  'logout': [];
}>();

const authStore = useAuthStore();
const { t } = useI18n();
const { canViewSubscription } = usePermissions();

const showAlerts = computed(() => {
  return platformFeatures.cloudOnly.subscription && 
         canViewSubscription.value && 
         hasRestaurantContext();
});
</script>
